import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useAuth } from 'react-oidc-context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { modalStore } from '../../../store/authStore/modalStore';
import { captureOAuthReturnUrl } from '../../constants/authRoutes';
import { promptStagingOAuthBlocked } from '../../../config/runtime-target';

export const DividerContainer = styled.div`
  ${tw`flex items-center my-4`}
`;

export const DividerLine = styled.div`
  ${tw`flex-1 h-px bg-gray-300`}
`;

export const DividerText = styled.span`
  ${tw`px-4 text-sm text-text-secondary`}
`;

const SocialButtonsContainer = styled.div`
  ${tw`flex justify-center items-center gap-4 my-6`}
`;

const SocialButton = styled.button`
  ${tw`w-14 h-14 rounded-full bg-bg-secondary flex items-center justify-center transition-all duration-200 cursor-pointer border-none shadow-card`}

  &:hover {
    ${tw`bg-bg-secondary shadow-card-hover`}
  }

  &:active {
    transform: scale(0.95);
  }
`;

/**
 * Sends the browser to the Cognito Hosted UI with Google as the identity
 * provider. A Google account that does not exist yet is created on the way,
 * so the sign-in and sign-up modals use the same button.
 */
export const useCognitoOAuthSignIn = () => {
  const auth = useAuth();
  return async (identityProvider: 'Google' | 'SignInWithApple') => {
    if (promptStagingOAuthBlocked()) {
      return;
    }
    captureOAuthReturnUrl();
    if (modalStore.pendingAction) {
      sessionStorage.setItem('pending_checkout', 'true');
    }
    await auth.signinRedirect({ extraQueryParams: { identity_provider: identityProvider } });
  };
};

interface GoogleSignInButtonProps {
  title: string;
  dataTestId: string;
}

export const GoogleSignInButton = ({ title, dataTestId }: GoogleSignInButtonProps) => {
  const signInWith = useCognitoOAuthSignIn();
  return (
    <SocialButtonsContainer>
      <SocialButton
        onClick={() => signInWith('Google')}
        title={title}
        aria-label={title}
        type="button"
        data-testid={dataTestId}
      >
        <FontAwesomeIcon icon={faGoogle} size="xl" style={{ color: '#DB4437' }} />
      </SocialButton>
    </SocialButtonsContainer>
  );
};
